import React, { useEffect } from 'react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import WizardProgressBar from './WizardProgressBar'

interface WizardModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  stepLabel: string
  totalSteps: number
  currentStep: number
  onBack?: () => void
  onNext: () => void
  nextLabel?: string
  nextDisabled?: boolean
  hideNext?: boolean
  errorMessage?: string
  children: React.ReactNode
}

const WizardModal = ({
  isOpen,
  onClose,
  title,
  stepLabel,
  totalSteps,
  currentStep,
  onBack,
  onNext,
  nextLabel = 'Next',
  nextDisabled = false,
  hideNext = false,
  errorMessage,
  children,
}: WizardModalProps) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div style={{
      position: 'absolute',
      top: 0, left: 0, right: 0,
      height: '100vh',
      zIndex: 100,
      backgroundColor: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '24px 32px 16px', flexShrink: 0, borderBottom: '1px solid #e5e7eb' }}>
        <div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0a0d47', margin: 0 }}>{title}</h2>
          <p style={{ fontSize: '0.8125rem', color: '#9ca3af', margin: '2px 0 0' }}>{stepLabel}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{ padding: '6px', borderRadius: '8px', border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}
          aria-label="Close"
        >
          <XMarkIcon style={{ width: 20, height: 20 }} />
        </button>
      </div>

      {/* Progress bar + content — centered and constrained */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: '32px 32px 0' }}>
        <div style={{ width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <WizardProgressBar totalSteps={totalSteps} currentStep={currentStep} />
          {children}
        </div>
      </div>

      {/* Footer */}
      {errorMessage && (
        <p style={{ color: '#E53935', fontSize: '0.8125rem', textAlign: 'center', margin: '0 32px 8px' }}>
          {errorMessage}
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', height: '64px', borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
        <button
          type="button"
          onClick={onBack ?? onClose}
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', padding: '9px 16.078px 8.391px 17px', borderRadius: '4px', border: '1px solid #E0E0E0', fontSize: '0.875rem', color: '#6b7280', background: 'none', cursor: 'pointer' }}
        >
          <ChevronLeftIcon style={{ width: 16, height: 16 }} />
          {onBack ? 'Back' : 'Cancel'}
        </button>
        {!hideNext && <button
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0,
            padding: nextLabel === 'Submit Request' ? '8px 20px' : '8px 20px 7.391px 19px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: '#2196F3',
            color: '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            opacity: nextDisabled ? 0.4 : 1,
            cursor: nextDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {nextLabel}
          {nextLabel !== 'Submit Request' && <ChevronRightIcon style={{ width: 16, height: 16 }} />}
        </button>}
      </div>

    </div>
  )
}

export default WizardModal
